/**
 * Cmd/Ctrl+K from anywhere in the app: land on the home page with the
 * composer focused (PRD-0271 D-5).
 *
 * Bound on `window` rather than on the composer or the layout's root node so
 * it reaches the operator wherever focus happens to be — inside a table, a
 * drawer, another page's search box. `preventDefault` runs before the
 * navigation because the browser's own find-in-page and address-bar bindings
 * sit on the same chord.
 *
 * @module hooks/useGlobalShortcut
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useComposerFocus } from '@/hooks/useComposerFocus'
import { hasOverlayAbove } from '@/utils/overlayLayers'

/**
 * Spelled literally rather than imported as ROUTES.HOME: this hook is mounted
 * by AppLayout, which routes/index.tsx imports to build the tree, so reading
 * the constant back out of that module would close a cycle between the route
 * table and the layout it mounts (the useClaimsRealtime precedent).
 */
const HOME_PATH = '/'

export function useGlobalShortcut(): void {
  const navigate = useNavigate()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== 'k') return
      if (!event.metaKey && !event.ctrlKey) return
      if (event.defaultPrevented || event.isComposing) return
      if (hasOverlayAbove('float')) return
      event.preventDefault()
      navigate(HOME_PATH)
      // Read through getState() rather than a subscribed selector: the token
      // is written here and only ever read by the composer, so subscribing
      // would re-run this effect on every request it made itself.
      useComposerFocus.getState().request()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [navigate])
}
